import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { CategoryTypeId } from '@diritto/common/content/constants';
import { Color } from '@diritto/common/style/color';
import { isArraysEqualIgnoreOrder } from '@diritto/common/utils/array';
import { ProfileSettingType } from '@diritto/common/account/constants/profile-setting.constant';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { GNBHeight, isPC } from 'constants/layout';
import { DotDivider } from 'ui-components/common/DotDivider';
import { useAuthState } from 'contexts/AuthProvider';
import { BottomButtonHeight, MobileBottomButtonHeight } from 'constants/button';
import { BottomButton } from 'ui-components/common/BottomButton';
import { showToast } from 'utils/modal';
import { ProfileFavoriteCategoryItem } from 'app-components/accounts/profiles/setting/favorite-category/ProfileFavoriteCategoryItem';
import { useUpdateProfileSettingMutation } from 'queries/profile/mutations/useUpdateProfileSettingMutation';
import { useAddProfileSettingMutation } from 'queries/profile/mutations/useAddProfileSettingMutation';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useClient } from 'ui-hooks/useClient';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { accountsQueryKey } from 'queries/account/queryKey';
import { relayQueryKey } from 'queries/relay/queryKey';
import { useHomeSettingDispatch, useHomeSettingState } from 'contexts/HomeSettingProvider';

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.ProfileSetting,
};

const FavoriteCategoryItemList = [
  {
    title: '모든 장르 볼래요 🎲',
    description: '다양한 장르를 골고루 보고싶어요.',
    categoryIds: [
      CategoryTypeId.Fantasy,
      CategoryTypeId.BL,
      CategoryTypeId.RomanceFantasy,
      CategoryTypeId.Romance,
      CategoryTypeId.Any,
    ],
  },
  {
    title: '로·로·판 위주로 볼래요 ✨',
    description: '로맨스, 로판, 판타지 중심으로 보여드릴게요.',
    categoryIds: [CategoryTypeId.Romance, CategoryTypeId.RomanceFantasy, CategoryTypeId.Fantasy, CategoryTypeId.Any],
  },
  {
    title: 'BL만 보고싶어요 👀',
    description: 'BL장르만 골라서 보여드릴게요.',
    categoryIds: [CategoryTypeId.BL],
  },
];

export const SettingFavoriteCategoryForm = () => {
  const { tracker } = useTrackingState();
  const { profile } = useAuthState();
  const { isApp } = useClient();
  const queryClient = useQueryClient();

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);

  const { mutate: updateProfileSetting } = useUpdateProfileSettingMutation();
  const { mutate: addProfileSetting } = useAddProfileSettingMutation();

  const { categoryIds: favoriteCategoryIds } = useHomeSettingState();
  const { changeCategoryIds: changeFavoriteCategoryIds } = useHomeSettingDispatch();

  const filterCategoryItems = useMemo(
    () =>
      FavoriteCategoryItemList.map((section, index) => ({
        ...section,
        checked: selectedIndex === index,
        onClick: () => setSelectedIndex(index),
      })),
    [selectedIndex],
  );

  useEffect(() => {
    if (selectedIndex !== null || currentIndex !== null) {
      return;
    }

    if (favoriteCategoryIds.length > 0) {
      const exactMatchIndex = FavoriteCategoryItemList.findIndex(item =>
        isArraysEqualIgnoreOrder(item.categoryIds, favoriteCategoryIds),
      );

      if (currentIndex === null && exactMatchIndex !== -1) {
        setSelectedIndex(exactMatchIndex);
        setCurrentIndex(exactMatchIndex);
      }
    }
  }, [currentIndex, selectedIndex, favoriteCategoryIds]);

  const handleChangeFavoriteCategoryIds = useCallback(
    (categoryIds: number[]) => {
      const handleSuccess = () => {
        if (isApp) {
          DirittoAppBridge.requestProfileSetting(ProfileSettingType.FavoriteCategory);
        } else {
          queryClient.invalidateQueries({ queryKey: relayQueryKey.relayContent });
          queryClient.invalidateQueries({ queryKey: accountsQueryKey.user });
        }
      };

      const profileSettingParams = {
        type: ProfileSettingType.FavoriteCategory,
        payload: {
          categoryIds,
        },
      };

      if (favoriteCategoryIds.length > 0) {
        updateProfileSetting(profileSettingParams, {
          onSuccess: () => {
            handleSuccess();
          },
        });
      } else {
        addProfileSetting(profileSettingParams, {
          onSuccess: () => {
            handleSuccess();
          },
        });
      }
    },
    [favoriteCategoryIds, updateProfileSetting, addProfileSetting, isApp, queryClient],
  );

  const handleSubmit = useCallback(() => {
    if (selectedIndex === null) {
      return;
    }

    if (selectedIndex === currentIndex) {
      showToast('현재 설정과 동일해요.');
      return;
    }

    const selectedCategoryIds = FavoriteCategoryItemList[selectedIndex].categoryIds;

    if (isApp && !profile) {
      DirittoAppBridge.openSign();
      setSelectedIndex(null);
      return;
    }

    if (!isApp && !profile) {
      changeFavoriteCategoryIds(selectedCategoryIds);
      showToast(`선호 장르가 적용되었어요.\n로그인하면 다른 기기에서도 이 설정이 유지돼요.`, {
        link: {
          url: '/accounts/sign-in',
          text: '로그인하기 >',
        },
      });
    } else {
      handleChangeFavoriteCategoryIds(selectedCategoryIds);
      showToast(`선호 장르가 적용되었어요.`);
    }

    tracker.track(TrackingEventType.ClickProfileSettingFavoriteCategory, {
      categoryIds: selectedCategoryIds,
      trackerExtraInfo,
    });
    setCurrentIndex(selectedIndex);
  }, [
    selectedIndex,
    currentIndex,
    profile,
    handleChangeFavoriteCategoryIds,
    tracker,
    isApp,
    changeFavoriteCategoryIds,
  ]);

  return (
    <StyledContainer>
      <div className="flex flex-col justify-center items-center text-center py-8 gap-1">
        <p className="text-black text-sm font-bold">내 취향에 맞게 설정해보세요.</p>
        <p className="text-gray-500 text-xxs font-medium">선택한 장르에 맞춰 콘텐츠를 구성해드릴게요.</p>
      </div>

      <div className="flex flex-col justify-center items-center w-full gap-2.5">
        {filterCategoryItems.map((item, index) => (
          <ProfileFavoriteCategoryItem key={`favorite-category-item-${index}`} {...item} />
        ))}
        <div className="flex flex-row justify-start items-start w-[358px] gap-1">
          <DotDivider />
          <p className="text-gray-500 text-xxxs font-medium">일부 콘텐츠 및 배너에는 적용되지 않을 수 있어요.</p>
        </div>
      </div>
      <BottomButton label="적용하기" onClick={handleSubmit} disabled={selectedIndex === currentIndex} />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${Color.Gray100};
  min-height: calc(100vh - ${MobileBottomButtonHeight}px);

  @media (${isPC}) {
    min-height: calc(100vh - ${GNBHeight}px - ${BottomButtonHeight}px);
  }
`;
